import { PERMISSION_LABELS } from "admin/components/user-management/AdminGrantsPanel";
import type {
  PendingGrantAddition,
  PendingGrantRemoval,
  PendingGrantUpdate,
} from "admin/types/interfaces/grant";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";

interface ConfirmGrantChangesDialogProps {
  open: boolean;
  // The user email or group name whose grants are being changed.
  resourceLabel: string;
  additions: PendingGrantAddition[];
  removals: PendingGrantRemoval[];
  updates: PendingGrantUpdate[];
  onConfirm: () => void;
  // Called whenever the dialog closes, including after onConfirm.
  onClose: () => void;
}

const ConfirmGrantChangesDialog = ({
  open,
  resourceLabel,
  additions,
  removals,
  updates,
  onConfirm,
  onClose,
}: ConfirmGrantChangesDialogProps) => (
  <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Apply grant overrides?</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-3">
            <p>
              You are about to change who can view, edit, or manage <strong>{resourceLabel}</strong>
              . Only do this for support troubleshooting.
            </p>
            <ul className="list-disc list-inside ml-4">
              {additions.map((a) => (
                <li key={`add-${a.type}-${a.identifier}`}>
                  Grant <strong>{a.label}</strong> {PERMISSION_LABELS[a.permission]}
                </li>
              ))}
              {updates.map((u) => (
                <li key={`update-${u.principal}-${u.oldPermission}`}>
                  Change <strong>{u.label}</strong> from {PERMISSION_LABELS[u.oldPermission]} to{" "}
                  {PERMISSION_LABELS[u.newPermission]}
                </li>
              ))}
              {removals.map((r) => (
                <li key={`remove-${r.principal}-${r.permission}`}>
                  Revoke {PERMISSION_LABELS[r.permission]} from <strong>{r.label}</strong>
                </li>
              ))}
            </ul>
            <p>
              Changes take effect immediately and are applied one at a time, so if one fails the
              others still apply.
            </p>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        {/* Don't forward the click event: a handler that calls preventDefault on it (e.g.
            react-hook-form's handleSubmit) stops Radix from closing the dialog. */}
        <AlertDialogAction
          onClick={() => onConfirm()}
          className="bg-destructive hover:bg-destructive/90"
        >
          Apply changes
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default ConfirmGrantChangesDialog;
